import { formatDate } from "../../utils";

export const computeIntersectionSearchLayoutFields = (repos, selectedRepoIds) => {
  const selectedRepos = (selectedRepoIds || [])
    .map((id) => repos?.[id])
    .filter(Boolean);

  if (selectedRepos.length === 0) return [];

  const counts = new Map();
  const fieldProperties = new Map(); // Store all field properties for intersection

  selectedRepos.forEach((repoEntry) => {
    (repoEntry?.searchLayoutFields || []).forEach((field) => {
      if (!field?.fieldName) return;

      const fieldName = field.fieldName;
      counts.set(fieldName, (counts.get(fieldName) || 0) + 1);

      // Store field properties for intersection logic
      if (!fieldProperties.has(fieldName)) {
        fieldProperties.set(fieldName, []);
      }
      fieldProperties.get(fieldName).push(field);
    });
  });

  const intersection = [];
  counts.forEach((count, fieldName) => {
    if (count === selectedRepos.length) {
      // Get all field instances for this fieldName across repos
      const fieldInstances = fieldProperties.get(fieldName);

      // Merge field properties with intersection logic
      const mergedField = mergeFieldProperties(fieldName, fieldInstances);

      if (mergedField) intersection.push(mergedField);
    }
  });

  return intersection;
};

// Function to compute common sortable fields across selected repositories
export const computeIntersectionSortableFields = (repos, selectedRepoIds) => {
  const selectedRepos = (selectedRepoIds || [])
    .map((id) => repos?.[id])
    .filter(Boolean);

  if (selectedRepos.length === 0) return [];

  const counts = new Map();
  const fieldProperties = new Map(); // Store all field properties for intersection

  selectedRepos.forEach((repoEntry) => {
    (repoEntry?.fields || []).forEach((field) => {
      if (!field?.fieldName || !field?.sortable) return; // Only consider sortable fields

      const fieldName = field.fieldName;
      counts.set(fieldName, (counts.get(fieldName) || 0) + 1);

      // Store field properties for intersection logic
      if (!fieldProperties.has(fieldName)) {
        fieldProperties.set(fieldName, []);
      }
      fieldProperties.get(fieldName).push(field);
    });
  });

  const intersection = [];
  counts.forEach((count, fieldName) => {
    if (count === selectedRepos.length) {
      // Get all field instances for this fieldName across repos
      const fieldInstances = fieldProperties.get(fieldName);

      // Merge field properties with intersection logic
      const mergedField = mergeFieldProperties(fieldName, fieldInstances);

      if (mergedField && mergedField.sortable) intersection.push(mergedField);
    }
  });

  return intersection;
};

// Helper function to merge field properties across repos
export const mergeFieldProperties = (fieldName, fieldInstances) => {

  if (!fieldInstances || fieldInstances.length === 0) return null;

  const firstField = fieldInstances[0];
  const mergedField = { ...firstField };

  // Compute intersection of validValues for all field types that have them
  if (firstField.validValues && Array.isArray(firstField.validValues)) {
    const allValidValues = fieldInstances.map(field => field.validValues || []);

    // Start with the first field's valid values and intersect with others
    let intersectionValues = [...allValidValues[0]];

    for (let i = 1; i < allValidValues.length; i++) {
      const currentValues = allValidValues[i];
      const currentValueMap = new Map(
        currentValues.map(v => [typeof v === 'string' ? v : v.value, v])
      );

      intersectionValues = intersectionValues.filter(v => {
        const key = typeof v === 'string' ? v : v.value;
        return currentValueMap.has(key);
      }).map(v => {
        const key = typeof v === 'string' ? v : v.value;
        return currentValueMap.get(key);
      });
    }

    // If no common valid values, don't include this field in filters
    if (intersectionValues.length === 0) {
      return null;
    }

    mergedField.validValues = intersectionValues;
  }

  // For DATE fields, check range support intersection
  if (firstField.type === 'DATE') {

    let allowLowRange = true;
    let allowHighRange = true;

    // Collect all min/max values for reference
    const allMinValues = [];
    const allMaxValues = [];

    // Only allow range if ALL repos support it
    fieldInstances.forEach((field, index) => {

      if (field.allowLowRange === false) allowLowRange = false;
      if (field.allowHighRange === false) allowHighRange = false;

      // Collect original values
      if (field.minValue) allMinValues.push(field.minValue);
      if (field.maxValue) allMaxValues.push(field.maxValue);
    });

    mergedField.allowLowRange = allowLowRange;
    mergedField.allowHighRange = allowHighRange;

    // Store original min/max values for reference
    mergedField.originalMinValues = allMinValues;
    mergedField.originalMaxValues = allMaxValues;

    // Merge min/max values (take most restrictive)
    if (allowLowRange || allowHighRange) {
      const minDateValues = allMinValues.map(date => new Date(date));
      const maxDateValues = allMaxValues.map(date => new Date(date));

      if (minDateValues.length > 0) {
        mergedField.minValue = new Date(Math.max(...minDateValues)).toISOString().split('T')[0];
      }
      if (maxDateValues.length > 0) {
        mergedField.maxValue = new Date(Math.min(...maxDateValues)).toISOString().split('T')[0];
      }
    }
  }
  return mergedField;
};

export const generateFilterChips = ({
  fields = [],
  filters = {},
  filtersConditions = {},
  FilterChange,
}) => {
  return fields
    .map((field) => {
      const { fieldName, type, label } = field;

      const value = filters[fieldName];
      const condition = filtersConditions?.[fieldName]?.value;

      // DATE TYPE
      if (type === 'DATE') {
        const start = filters[`${fieldName}Start`];
        const end = filters[`${fieldName}End`];
        const relDateVal = filters[`${fieldName}RelDateVal`];
        const relDateUnit = filtersConditions?.[`${fieldName}RelDate`]?.label;

        const isRange =
          (field.allowLowRange || field.allowHighRange) &&
          condition === 'range';

        // RELATIVE DATE (withIn condition)
        if (condition === 'withIn' && relDateVal && relDateUnit) {
          const duration = Math.abs(parseInt(relDateVal));
          return {
            key: fieldName,
            label,
            value: `With in ${duration} ${relDateUnit}`,
            onClear: () => {
              FilterChange?.(`${fieldName}RelDateVal`, null);
              FilterChange?.(fieldName, null);
            }
          };
        }

        // RANGE
        if (isRange && (start || end)) {
          return {
            key: fieldName,
            label,
            value: `${start ? formatDate(start, "MMM DD, YYYY") : '...'} - ${end ? formatDate(end, "MMM DD, YYYY") : '...'
              }`,
            onClear: () => {
              FilterChange?.(`${fieldName}Start`, null);
              FilterChange?.(`${fieldName}End`, null);
            }
          };
        }

        // SINGLE DATE
        if (value) {
          const conditionLabel =
            filtersConditions?.[fieldName]?.label || '';

          return {
            key: fieldName,
            label,
            value: conditionLabel
              ? `${conditionLabel} - ${formatDate(value, "MMM DD, YYYY")}`
              : formatDate(value, "MMM DD, YYYY"),
            onClear: () => {
              FilterChange?.(fieldName, null);
            }
          };
        }

        return null;
      }

      // DEFAULT (STRING / TERMS / ANY)
      if (value) {
        const displayValue =
          typeof value === 'object' && value !== null
            ? value.label ?? value.value
            : value;

        return {
          key: fieldName,
          label,
          value: displayValue,
          onClear: () => {
            FilterChange?.(fieldName, null);
          }
        };
      }

      return null;
    })
    .filter(Boolean);
};

// Helper function to capitalize first character
export const capitalizeFirst = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}; 
